import React, { useState, useEffect } from 'react';
import { Phone, X, Loader2, Save, Send, PhoneOff, Clock } from 'lucide-react';
import { supabase_save_candidate_communication, supabase } from '../services/supabaseService';
import { summarizeCallTranscript } from '../services/geminiService';

interface CallChecklist {
    id: string;
    title: string;
    items: string[];
}

interface CallSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    candidate: any;
    profileId: string;
    currentUser: any;
    onCommunicationSaved?: () => void;
    editCommId?: string;
    initialContent?: string;
}

export const CallSidebar: React.FC<CallSidebarProps> = ({ isOpen, onClose, candidate, profileId, currentUser, onCommunicationSaved, editCommId, initialContent }) => {
    const [recruiterPhone, setRecruiterPhone] = useState(currentUser?.phone || '+31619944314');
    const [callState, setCallState] = useState<'setup' | 'initiating' | 'ringing' | 'active' | 'ended' | 'missed'>('setup');
    const [birdCallId, setBirdCallId] = useState<string | null>(null);

    // AI Summarization State
    const [isWaitingForTranscript, setIsWaitingForTranscript] = useState(false);
    const [activeCommId, setActiveCommId] = useState<string | null>(null);

    // Timer state
    const [callDuration, setCallDuration] = useState(0);
    const [callTimer, setCallTimer] = useState<NodeJS.Timeout | null>(null);

    // Notes
    const [callNotes, setCallNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Raw Transcript display
    const [rawTranscriptContent, setRawTranscriptContent] = useState<string | null>(null);
    const [showRawTranscript, setShowRawTranscript] = useState(false);

    // Checklist
    const [checklists, setChecklists] = useState<CallChecklist[]>([]);
    const [selectedChecklist, setSelectedChecklist] = useState<CallChecklist | null>(null);
    const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});

    // Manual Stop Dialog
    const [showConfirmStop, setShowConfirmStop] = useState(false);

    // Realtime WebSocket Listener for Bird Webhook Status
    useEffect(() => {
        if (!isOpen || !profileId) return;

        const formatE164 = (p: string) => {
            let cleaned = String(p).replace(/\(0\)/g, '').replace(/[^0-9+]/g, '');
            if (cleaned.startsWith('00')) cleaned = '+' + cleaned.substring(2);
            else if (cleaned.startsWith('06')) cleaned = '+31' + cleaned.substring(1);
            else if (cleaned.startsWith('0') && !cleaned.startsWith('00')) cleaned = '+31' + cleaned.substring(1);
            else if (!cleaned.startsWith('+')) cleaned = '+' + cleaned;
            cleaned = cleaned.replace(/^\+310/, '+31');
            return cleaned;
        };

        const channel = supabase.channel('system_call_updates')
            .on('broadcast', { event: 'status_change' }, ({ payload }) => {
                const currentCptPhone = candidate?.phone ? formatE164(candidate.phone) : null;
                if (payload.profileId === profileId || (payload.candidatePhone && currentCptPhone && payload.candidatePhone === currentCptPhone)) {
                    const status = payload.status; // 'ringing', 'answered', 'ended', 'failed'
                    if (status === 'ringing') {
                        setCallState('ringing');
                    } else if (status === 'answered') {
                        setCallState('active');
                        // Start Timer only when picked up
                        const timer = setInterval(() => setCallDuration(prev => prev + 1), 1000);
                        setCallTimer(timer);
                    } else if (status === 'ended' || status === 'completed') {
                        setCallState('ended');
                        // Timer cleanup happens via useEffect relying on callState or manually 
                        setCallTimer(prev => { if (prev) clearInterval(prev); return null; });
                    } else if (status === 'failed' || status === 'missed') {
                        setCallState('missed');
                        setCallTimer(prev => { if (prev) clearInterval(prev); return null; });
                    }
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [isOpen, profileId]);

    useEffect(() => {
        if (!isOpen) {
            resetState();
        } else {
            if (editCommId && initialContent) {
                // We are opening in Edit mode
                setCallState('ended');
                setActiveCommId(editCommId);

                // Try to strip the automatic prefix for the text area
                let cleanedNotes = initialContent;
                let rawText = null;

                // Extract raw transcript if present
                if (cleanedNotes.includes('---RAW_TRANSCRIPT---')) {
                    const parts = cleanedNotes.split('---RAW_TRANSCRIPT---');
                    cleanedNotes = parts[0];
                    rawText = parts[1].trim();
                }

                if (cleanedNotes.includes('🤖 **Automatische Call Transcriptie:**\n\n')) {
                    cleanedNotes = cleanedNotes.replace('🤖 **Automatische Call Transcriptie:**\n\n', '');
                } else if (cleanedNotes.includes('Notities:\n')) {
                    cleanedNotes = cleanedNotes.split('Notities:\n')[1] || cleanedNotes;
                }

                setCallNotes(cleanedNotes.trim());
                setRawTranscriptContent(rawText);
            } else {
                resetState();
            }

            if (currentUser?.clientId || currentUser?.client_id) {
                fetchChecklists(currentUser.clientId || currentUser.client_id);
            }
        }
    }, [isOpen, currentUser, editCommId, initialContent]);

    // Polling effect for transcript
    useEffect(() => {
        let pollTimer: NodeJS.Timeout;

        const checkTranscript = async () => {
            if (!activeCommId || !isWaitingForTranscript) return;

            try {
                const { data, error } = await supabase
                    .from('candidate_communications')
                    .select('content')
                    .eq('id', activeCommId)
                    .single();

                if (data && data.content && !data.content.includes('Wachten op samenvatting')) {
                    // Transcript arrived! The trigger updated the content
                    setIsWaitingForTranscript(false);

                    let rawContent = data.content;
                    if (data.content.includes('---RAW_TRANSCRIPT---')) {
                        rawContent = data.content.split('---RAW_TRANSCRIPT---')[1].trim();
                        setRawTranscriptContent(rawContent);
                    } else {
                        // Fallback purely for safety if the old trigger format was caught
                        rawContent = data.content.split('\n\n').slice(1).join('\n\n');
                    }

                    if (rawContent.trim().length > 10) {
                        try {
                            // Fetch summary from Gemini
                            const summary = await summarizeCallTranscript(rawContent);
                            setCallNotes(summary);
                        } catch (aiErr) {
                            console.error("AI summarization failed:", aiErr);
                            // Fallback to raw text if AI fails
                            setCallNotes("AI Samenvatting mislukt. Verwerk hieronder je notities.");
                        }
                    }
                }
            } catch (err) {
                console.error("Error polling transcript:", err);
            }
        };

        if (isWaitingForTranscript && activeCommId) {
            pollTimer = setInterval(checkTranscript, 3000);
        }

        return () => {
            if (pollTimer) clearInterval(pollTimer);
        };
    }, [isWaitingForTranscript, activeCommId]);

    const fetchChecklists = async (clientId: string) => {
        try {
            const { data } = await supabase
                .from('company_call_checklists')
                .select('*')
                .eq('client_id', clientId)
                .order('created_at', { ascending: true });
            if (data) setChecklists(data);
        } catch (e) {
            console.error('Failed to load checklists', e);
        }
    };

    useEffect(() => {
        return () => {
            if (callTimer) clearInterval(callTimer);
        };
    }, [callTimer]);

    const resetState = () => {
        setCallState('setup');
        setCallDuration(0);
        setCallNotes('');
        setIsSaving(false);
        setSelectedChecklist(null);
        setCheckedItems({});
        setBirdCallId(null);
        setIsWaitingForTranscript(false);
        setActiveCommId(null);
        setRawTranscriptContent(null);
        setShowRawTranscript(false);
        if (callTimer) {
            clearInterval(callTimer);
            setCallTimer(null);
        }
    };

    const formatDuration = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const handleInitiateCall = async () => {
        if (!recruiterPhone || !candidate?.phone) return;
        setCallState('initiating');
        try {
            const formatE164 = (p: string) => {
                let cleaned = String(p).replace(/\(0\)/g, '').replace(/[^0-9+]/g, '');
                if (cleaned.startsWith('00')) cleaned = '+' + cleaned.substring(2);
                else if (cleaned.startsWith('06')) cleaned = '+31' + cleaned.substring(1);
                else if (cleaned.startsWith('0') && !cleaned.startsWith('00')) cleaned = '+31' + cleaned.substring(1);
                else if (!cleaned.startsWith('+')) cleaned = '+' + cleaned;
                cleaned = cleaned.replace(/^\+310/, '+31'); // Fix commonly mistyped +3106 -> +316
                return cleaned;
            };
            const rPhone = formatE164(recruiterPhone);
            const cPhone = formatE164(candidate.phone);
            console.log(`[CallSidebar] Initiating call from ${rPhone} to candidate ${cPhone} (Candidate: ${candidate.name})`);

            // Direct Webhook Trigger as requested
            const webhookUrl = 'https://capture.eu-west-1.nest.messagebird.com/webhooks/2a1440f2-3f27-4173-aa9b-4c147a01ec6c/b3f06193-cb8d-4b8b-ad0d-3bf0cfde345c';

            const res = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company_number: "+31303100468",
                    recruiter_number: rPhone,
                    candidate_number: cPhone,
                    destination: cPhone, // Fallback variable for flow compatibility
                    profileId: profileId,
                    userId: currentUser?.id,
                    appUrl: window.location.origin // Critically needed for the Bird flow to attach the transcription hook
                })
            });

            if (!res.ok) {
                throw new Error('WebHook trigger mislukt');
            }

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                data = await res.json();
            } else {
                data = await res.text();
            }

            console.log("Bird Webhook triggered successfully:", data);

            // Webhooks typically don't return a synchronous Call ID we can track/cancel
            setBirdCallId(null);

            // Call initiated successfully, we now wait for realtime broadcast to move to 'ringing' or 'active'
            setCallState('initiating');

        } catch (e: any) {
            console.error('Call failed:', e);
            alert('Bellen is mislukt: ' + e.message);
            setCallState('setup');
        }
    };

    const handleManualStop = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowConfirmStop(true);
    };

    const confirmManualStop = () => {
        setShowConfirmStop(false);
        handleEndCall();
    };

    const handleEndCall = async () => {
        if (callTimer) clearInterval(callTimer);
        setCallState('ended');

        // Terminate the physical Bird call if we have an ID
        if (birdCallId) {
            try {
                const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                    ? `/api/bird/call/${birdCallId}`
                    : `${window.location.protocol}//${window.location.hostname}:4000/api/bird/call/${birdCallId}`;

                await fetch(apiUrl, { method: 'DELETE' });
            } catch (e) {
                console.error("Failed to terminate bird call:", e);
            }
        }

        // Insert temporary "Waiting for transcript" note
        try {
            console.log("Inserting pending transcript note...");
            setIsWaitingForTranscript(true);
            const contentString = `🤖 **Automatische Call Transcriptie:**\n\n⏳ Wachten op samenvatting van Bird...`;

            // Resolve FK safely
            let actualProfileId = profileId;
            const { data: cCheck } = await supabase.from('candidates').select('id').eq('id', profileId).limit(1).maybeSingle();
            if (cCheck) actualProfileId = cCheck.id;

            // We use standard supabase insert to get the inserted row ID easily
            const { data: newComm, error } = await supabase
                .from('candidate_communications')
                .insert([{
                    profile_id: actualProfileId,
                    user_id: currentUser?.id || null,
                    content: contentString,
                    type: 'call'
                }])
                .select()
                .single();

            if (error) throw error;
            if (newComm?.id) {
                setActiveCommId(newComm.id);
            }
            if (onCommunicationSaved) onCommunicationSaved();
        } catch (e) {
            console.error("Failed to insert pending transcript note:", e);
            setIsWaitingForTranscript(false);
        }
    };

    const handleSaveNotes = async () => {
        if (!callNotes.trim() && callDuration === 0) {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            const durationStr = callDuration > 0 ? ` (Duur: ${formatDuration(callDuration)})` : '';

            let checklistStr = '';
            if (selectedChecklist) {
                checklistStr = `📋 Checklist: ${selectedChecklist.title}\n`;
                selectedChecklist.items.forEach((item, idx) => {
                    const status = checkedItems[idx] ? '[x]' : '[ ]';
                    checklistStr += `${status} ${item}\n`;
                });
                checklistStr += '\n';
            }

            let contentToSave = `${callNotes.trim() ? 'Notities:\n' + callNotes : '📞 Uitgaand telefoongesprek' + durationStr + '\n\n' + checklistStr}`;

            if (durationStr || checklistStr) {
                contentToSave = `📞 Uitgaand telefoongesprek${durationStr}\n\n${checklistStr}${callNotes.trim() ? 'Notities:\n' + callNotes : ''}`;
            }

            // Always append the raw transcript seamlessly so it stays hidden in the timeline but available for edit
            if (rawTranscriptContent) {
                contentToSave += `\n\n---RAW_TRANSCRIPT---\n${rawTranscriptContent}`;
            }

            if (editCommId) {
                // Update the specifically edited ID
                await supabase
                    .from('candidate_communications')
                    .update({ content: contentToSave })
                    .eq('id', editCommId);
            } else if (activeCommId) {
                // We update the existing row we placed when hanging up
                await supabase
                    .from('candidate_communications')
                    .update({ content: contentToSave })
                    .eq('id', activeCommId);
            } else {
                // Resolve FK safely
                let actualProfileId = profileId;
                const { data: cCheck } = await supabase.from('candidates').select('id').eq('id', profileId).limit(1).maybeSingle();
                if (cCheck) actualProfileId = cCheck.id;

                // Standard insert if no pending call active
                await supabase_save_candidate_communication(
                    actualProfileId,
                    currentUser?.id || 'unknown',
                    contentToSave,
                    'call'
                );
            }

            if (onCommunicationSaved) onCommunicationSaved();
            onClose();
        } catch (e) {
            console.error("Failed to save call notes", e);
            alert("Kon notities niet opslaan.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Sidebar (Global Floating) */}
            <div className="fixed top-0 right-0 h-full w-[320px] shrink-0 border-l border-slate-200 bg-slate-50 flex flex-col animate-slideLeft shadow-[0_0_40px_rgba(0,0,0,0.1)] z-[500]">
                {/* Header */}
                <div className="px-4 py-3 bg-white border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm ${callState === 'active' ? 'bg-red-500 animate-pulse' :
                            callState === 'initiating' ? 'bg-amber-500' :
                                'bg-emerald-500'
                            }`}>
                            <Phone size={14} />
                        </div>
                        <div>
                            <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest leading-none">
                                {editCommId ? 'Notities Bewerken' :
                                    callState === 'active' ? 'Live Gesprek' :
                                        callState === 'ended' ? 'Gesprek Afgerond' : 'Nieuw Gesprek'}
                            </h2>
                            <p className="text-[10px] text-slate-500 font-medium mt-0.5">Met {candidate?.name}</p>
                        </div>
                    </div>
                    {callState !== 'active' && callState !== 'initiating' && (
                        <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 rounded-full transition-colors">
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar flex flex-col gap-4">
                    {/* Status Banner */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm text-center flex flex-col items-center">
                        {callState === 'setup' && (
                            <>
                                <Phone size={32} className="text-emerald-500 mb-3" />
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Klaar om te bellen</h3>
                                <p className="text-xs text-slate-500 font-medium">Controleer je nummer en start het gesprek.</p>
                            </>
                        )}
                        {callState === 'initiating' && (
                            <>
                                <Loader2 size={32} className="text-amber-500 mb-3 animate-spin" />
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Verbinden...</h3>
                                <p className="text-xs text-slate-500 font-medium">Check je telefoon, je wordt nu gebeld.</p>
                            </>
                        )}
                        {callState === 'ringing' && (
                            <>
                                <Phone size={32} className="text-blue-500 mb-3 animate-bounce" />
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Overgaan...</h3>
                                <p className="text-xs text-slate-500 font-medium">Wachten tot de kandidaat opneemt.</p>
                            </>
                        )}
                        {callState === 'active' && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-3">
                                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                                        <Clock size={24} className="text-red-500" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-black text-slate-900 font-mono mb-1 tracking-widest">
                                    {formatDuration(callDuration)}
                                </h3>
                                <p className="text-xs text-red-500 font-bold uppercase tracking-widest animate-pulse">Live verbinding</p>
                            </>
                        )}
                        {callState === 'ended' && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                    <PhoneOff size={24} className="text-slate-400" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Gesprek beëindigd</h3>
                                <p className="text-xs text-slate-500 font-medium font-mono">Duur: {formatDuration(callDuration)}</p>
                            </>
                        )}
                        {callState === 'missed' && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-3">
                                    <PhoneOff size={24} className="text-red-500" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Geen gehoor</h3>
                                <p className="text-xs text-slate-500 font-medium">Gesprek gemist of weggedrukt.</p>
                            </>
                        )}
                    </div>

                    {/* Configuration / Notes Area */}
                    {callState === 'setup' && (
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                            <div>
                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Telefoon Kandidaat</label>
                                <div className="p-2 bg-slate-50 border border-slate-100 rounded-lg font-mono text-xs font-bold text-slate-700">
                                    {candidate?.phone || 'Geen nummer gevonden'}
                                </div>

                            </div>
                            <div>
                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Jouw Nummer (Recruiter)</label>
                                <input
                                    type="text"
                                    value={recruiterPhone}
                                    onChange={(e) => setRecruiterPhone(e.target.value)}
                                    className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-200 rounded-lg p-2 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all font-mono"
                                    placeholder="+31612345678"
                                />
                                <p className="text-[9px] text-slate-400 mt-1 font-medium leading-tight">
                                    Systeem belt eerst jou. Zodra je opneemt, wordt de kandidaat gebeld met dit nummer.
                                </p>
                            </div>

                            <div>
                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><Clock size={10} /> Actieve Checklist (Optioneel)</label>
                                {checklists.length > 0 ? (
                                    <select
                                        className="w-full text-xs font-bold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none focus:border-emerald-500 transition-all font-mono"
                                        value={selectedChecklist?.id || ''}
                                        onChange={(e) => {
                                            const cl = checklists.find(c => c.id === e.target.value) || null;
                                            setSelectedChecklist(cl);
                                            setCheckedItems({});
                                        }}
                                    >
                                        <option value="">Geen checklist gebruiken</option>
                                        {checklists.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                    </select>
                                ) : (
                                    <div className="text-[10px] text-slate-500 bg-slate-50 p-2 rounded-lg border border-dashed border-slate-200">
                                        Je hebt nog geen checklists gemaakt. (Avatar ➔ Instellingen)
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {(callState === 'active' || callState === 'ended') && (
                        <div className="flex-1 flex flex-col gap-3 animate-fadeIn">

                            {selectedChecklist && (
                                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm shrink-0">
                                    <h4 className="text-xs font-black text-slate-900 mb-2 flex items-center gap-1">
                                        <Clock size={12} className="text-emerald-500" />
                                        {selectedChecklist.title}
                                    </h4>
                                    <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1 custom-scrollbar">
                                        {selectedChecklist.items.map((item, idx) => (
                                            <label key={idx} className="flex items-start gap-2 cursor-pointer group">
                                                <div className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${checkedItems[idx] ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-slate-50 border-slate-300 group-hover:border-emerald-500'}`}>
                                                    {checkedItems[idx] && <span className="text-[8px]">✔</span>}
                                                </div>
                                                <span className={`text-[11px] leading-tight ${checkedItems[idx] ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>{item}</span>
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={checkedItems[idx] || false}
                                                    onChange={(e) => setCheckedItems(prev => ({ ...prev, [idx]: e.target.checked }))}
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Transcript Waiting Indicator */}
                            {isWaitingForTranscript && (
                                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-start gap-3 shrink-0">
                                    <Loader2 size={16} className="text-indigo-500 mt-0.5 animate-spin shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-bold text-indigo-900 mb-0.5">Wachten op transcriptie</h4>
                                        <p className="text-xs text-indigo-700">Bird werkt de audio uit. Froukje stelt direct een samenvatting op zodra het binnen is.</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex-1 flex flex-col">
                                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                                    Vrije Notities {isWaitingForTranscript && <span className="text-indigo-500">(Auto-fill actief)</span>}
                                </label>
                                <textarea
                                    value={callNotes}
                                    onChange={(e) => setCallNotes(e.target.value)}
                                    placeholder="Typ hier live mee tijdens het gesprek..."
                                    className="flex-1 w-full bg-white border border-slate-200 focus:border-red- focus:ring-1 focus:ring-red- rounded-lg p-3 text-xs text-slate-700 outline-none transition-all resize-none shadow-sm min-h-[150px]"
                                    autoFocus
                                />

                                {/* Raw transcript toggle (Moved here for edit mode) */}
                                {rawTranscriptContent && (
                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <button
                                            onClick={() => setShowRawTranscript(!showRawTranscript)}
                                            className="text-[10px] font-bold text-slate-500 hover:text-slate-800 uppercase tracking-widest flex items-center gap-2 mb-2 transition-colors"
                                        >
                                            {showRawTranscript ? 'Oorspronkelijk Transcript Verbergen' : '👁️ Toon Volledige Transcript'}
                                        </button>

                                        {showRawTranscript && (
                                            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs text-slate-500 font-medium whitespace-pre-wrap max-h-48 overflow-y-auto custom-scrollbar italic shadow-inner">
                                                {rawTranscriptContent}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-4 bg-white border-t border-slate-100 shrink-0">
                    {showConfirmStop && (callState === 'initiating' || callState === 'ringing' || callState === 'active') ? (
                        <div className="bg-red-50 p-3 rounded-xl border border-red-100">
                            <p className="text-xs font-bold text-red-900 mb-2 text-center">Gesprek afbreken, weet je het zeker?</p>
                            <div className="flex gap-2">
                                <button 
                                    onClick={() => setShowConfirmStop(false)}
                                    className="flex-1 py-2 text-[10px] font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors uppercase tracking-widest"
                                >
                                    Nee
                                </button>
                                <button 
                                    onClick={confirmManualStop}
                                    className="flex-1 py-2 text-[10px] font-black text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm transition-colors uppercase tracking-widest"
                                >
                                    Ja, Stop
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {callState === 'setup' && (
                                <button
                                    onClick={handleInitiateCall}
                                    disabled={!recruiterPhone.trim()}
                                    className="w-full py-3 rounded-lg bg-emerald-600 text-white font-black text-[10px] uppercase tracking-widest hover:bg-emerald-700 shadow-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
                                >
                                    <Phone size={14} /> Start Gesprek
                                </button>
                            )}

                            {callState === 'initiating' && (
                                <div className="flex flex-col gap-2">
                                    <button
                                        disabled
                                        className="w-full py-3 rounded-lg bg-emerald-600/50 text-white font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 cursor-not-allowed"
                                    >
                                        <Loader2 size={14} className="animate-spin" /> Verbinden...
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleManualStop}
                                        className="w-full py-2 rounded-lg text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:bg-slate-50 hover:text-red-500 transition-all flex items-center justify-center gap-1"
                                    >
                                        <X size={12} /> Handmatig Afbreken
                                    </button>
                                </div>
                            )}

                            {callState === 'ringing' && (
                                <button
                                    type="button"
                                    onClick={handleManualStop}
                                    className="w-full py-3 rounded-lg bg-red-400 text-white font-black text-[10px] uppercase tracking-widest hover:bg-red-500 shadow-sm transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                                >
                                    <PhoneOff size={14} /> Stop Gesprek (Overgaan annuleren)
                                </button>
                            )}

                            {callState === 'active' && (
                                <button
                                    type="button"
                                    onClick={handleManualStop}
                                    className="w-full py-3 rounded-lg bg-red-500 text-white font-black text-[10px] uppercase tracking-widest hover:bg-red-600 shadow-sm transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                                >
                                    <PhoneOff size={14} /> Gesprek Ophangen
                                </button>
                            )}

                            {callState === 'ended' && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={onClose}
                                        className="py-3 px-4 rounded-lg bg-slate-100 text-slate-600 font-bold text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all"
                                    >
                                        Annuleren
                                    </button>
                                    <button
                                        onClick={handleSaveNotes}
                                        disabled={isSaving}
                                        className="flex-1 py-3 rounded-lg bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest hover:bg-black shadow-sm transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50"
                                    >
                                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                        Opslaan & Sluiten
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
